import React, { useState, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { useGeneral } from '../../../contexts';
import { useEvents } from '../../../hooks/useEvents';
import Services from '../../../services/Services';
import Button from '../../../components/ui/Button/Button';
import LoadingSkeleton from '../../../components/ui/LoadingSkeleton/LoadingSkeleton';
import { Tabs, Tab } from '../../../components/ui/Tabs';
import { Table, TableHeader, TableBody, TableRow, TableCell, EmptyTableRow } from '../../../components/ui/Table';
import FormInput from '../../../components/ui/Form/FormInput';
import transitionStyles from '../../../styles/transitions.module.css';
import FormTextarea from '../../../components/ui/Form/FormTextarea';
import FormField from '../../../components/ui/Form/FormField';
import { RichTextEditor } from '../../../components/ui/RichTextEditor';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import TimePicker from '../../../components/ui/TimePicker/TimePicker';
import ImageUploadModal from '../../../components/ui/ImageUploadModal/ImageUploadModal';
import GooglePlacesAutocomplete from '../../../components/ui/GooglePlacesAutocomplete/GooglePlacesAutocomplete';
import GoogleMap from '../../../components/ui/GoogleMap/GoogleMap';
import Modal from '../../../components/ui/Modal/Modal';
import { EventListItem, CoverFile, TicketPayload, ParticipationType } from '../../../types';
import { formatDateShort, formatTime } from '../../../utils/dateFormatters';
import { HiUsers } from 'react-icons/hi';
import { generateEventAdminUrl, createSlug } from '../../../utils/urlSlug';
import { combineDateTime } from '../../../utils/dateTimeFormatters';
import { convertFileToBase64 } from '../../../utils/fileConversion';
import { addHoursToTimeWithDayCheck } from '../../../utils/timeFormatters';
import { hasHtmlContent } from '../../../utils/htmlUtils';
import formStyles from '../../../components/ui/Form/Form.module.css';
import styles from './OrganizerEventsPage.module.css';

import { OrganizerPanelOutletContext } from '../../../types/organizerPanel.types';

const LCV_TICKET_TYPE = 'togather-join-ticket';

const isLcvJoinTicketType = (type?: string) =>
  (type || '').trim().toLowerCase() === LCV_TICKET_TYPE;

const createLcvJoinTicket = (): TicketPayload => ({
  title: 'LCV',
  type: LCV_TICKET_TYPE,
  description: 'Auto-generated ticket for LCV participation.',
  price: 0,
  currency: 'TRY',
  trackInventory: false,
});

const OrganizerEventsPage: React.FC = () => {
  const { channel, channelTitle } = useOutletContext<OrganizerPanelOutletContext>();
  const navigate = useNavigate();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation, language: currentLanguage } = language;
  
  const [activeTab, setActiveTab] = useState<'current' | 'past'>('current');
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCoverModal, setShowCoverModal] = useState(false);
  const currentTimestamp = Math.floor(Date.now() / 1000);

  // Form state
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isOnline, setIsOnline] = useState(false);
  const [website, setWebsite] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [locationAddressDefinition, setLocationAddressDefinition] = useState('');
  const [locationLatitude, setLocationLatitude] = useState<number>(0);
  const [locationLongitude, setLocationLongitude] = useState<number>(0);
  const [city, setCity] = useState('');
  const [ticketSaleStartDateTime, setTicketSaleStartDateTime] = useState('');
  const [ticketSaleTime, setTicketSaleTime] = useState('');
  const [participationType, setParticipationType] = useState<ParticipationType>('ticket');
  const [hasCertificate, setHasCertificate] = useState(false);

  const [isPublic, setIsPublic] = useState<"0" | "1" | "3">("1");
  const [eventStartDate, setEventStartDate] = useState('');
  const [eventStartTime, setEventStartTime] = useState('');
  const [eventEndDate, setEventEndDate] = useState('');
  const [eventEndTime, setEventEndTime] = useState('');
  const [coverImage, setCoverImage] = useState<string | null>(null);
  const [coverFile, setCoverFile] = useState<CoverFile | null>(null);

  // Tickets state
  const [tickets, setTickets] = useState<TicketPayload[]>([]);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [editingTicketIndex, setEditingTicketIndex] = useState<number | null>(null);
  const [ticketFormData, setTicketFormData] = useState<{
    title: string;
    type: string;
    description: string;
    price: string;
    currency: string;
    trackInventory: boolean;
    inventory: string;
  }>({
    title: '',
    type: 'normal',
    description: '',
    price: '0',
    currency: 'TRY',
    trackInventory: false,
    inventory: '0',
  });

  // Form validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Fetch events for this channel
  const { events, loading } = useEvents({
    token: token || null,
    channelID: channel?.channelID || '',
  });

  // Separate events into current and past
  const { currentEvents, pastEvents } = useMemo(() => {
    const current: EventListItem[] = [];
    const past: EventListItem[] = [];

    events.forEach((event) => {
      const endTime = event.eventEndDateTime || 0;
      if (endTime >= currentTimestamp) {
        current.push(event);
      } else {
        past.push(event);
      }
    });

    return { currentEvents: current, pastEvents: past };
  }, [events, currentTimestamp]);

  const displayedEvents = activeTab === 'current' ? currentEvents : pastEvents;

  const handleCreateEvent = () => {
    setIsCreateFormOpen(true);
    setActiveTab('current'); // Deselect tabs by keeping current but form will be shown
  };

  const handleCancelCreate = () => {
    setIsCreateFormOpen(false);
    // Reset form
    setTitle('');
    setDescription('');
    setIsOnline(false);
    setWebsite('');
    setLocationAddress('');
    setLocationAddressDefinition('');
    setLocationLatitude(0);
    setLocationLongitude(0);
    setCity('');
    setTicketSaleStartDateTime('');
    setTicketSaleTime('');
    setParticipationType('ticket');
    setHasCertificate(false);

    setIsPublic("1");
    setEventStartDate('');
    setEventStartTime('');
    setEventEndDate('');
    setEventEndTime('');
    setCoverImage(null);
    setCoverFile(null);
    setTickets([]);
    setErrors({});
  };

  const handleAddTicket = () => {
    if (participationType === 'rsvp') {
      toastError(
        getTranslation('organizerPanel.rsvpDisablesTickets') ||
        'LCV seciliyken ozel bilet ekleyemezsiniz.'
      );
      return;
    }

    setEditingTicketIndex(null);
    setTicketFormData({
      title: '',
      type: 'normal',
      description: '',
      price: '0',
      currency: 'TRY',
      trackInventory: false,
      inventory: '0',
    });
    setShowTicketModal(true);
  };

  const handleEditTicket = (index: number) => {
    const ticket = tickets[index];
    setEditingTicketIndex(index);
    setTicketFormData({
      title: ticket.title,
      type: ticket.type,
      description: ticket.description,
      price: String(ticket.price),
      currency: ticket.currency,
      trackInventory: ticket.trackInventory,
      inventory: String(ticket.inventory || 0),
    });
    setShowTicketModal(true);
  };

  const handleDeleteTicket = (index: number) => {
    setTickets(tickets.filter((_, i) => i !== index));
  };

  const handleSaveTicket = () => {
    if (participationType === 'rsvp') {
      toastError(
        getTranslation('organizerPanel.rsvpDisablesTickets') ||
        'LCV seciliyken ozel bilet ekleyemezsiniz.'
      );
      return;
    }

    if (!ticketFormData.title.trim()) {
      toastError(getTranslation('organizerPanel.ticketTitleRequired') || 'Bilet başlığı gereklidir');
      return;
    }
    if (!ticketFormData.description.trim()) {
      toastError(getTranslation('organizerPanel.ticketDescriptionRequired') || 'Ticket description is required.');
      return;
    }
    if (ticketFormData.type.trim().toLowerCase() === 'sponsor') {
      toastError(getTranslation('organizerPanel.ticketTypeSponsorNotAllowed') || 'Bilet tipi "sponsor" olamaz');
      return;
    }
    if (isLcvJoinTicketType(ticketFormData.type)) {
      toastError(
        getTranslation('organizerPanel.ticketTypeReservedName') ||
        '"togather-join-ticket" rezerv bir bilet tipi adıdır.'
      );
      return;
    }
    if (ticketFormData.trackInventory && (!ticketFormData.inventory || parseInt(ticketFormData.inventory) < 0)) {
      toastError(getTranslation('organizerPanel.inventoryRequired') || 'Envanter bilgisi gereklidir');
      return;
    }

    const cleanedPrice = ticketFormData.price.replace(/[^\d.]/g, '');
    const price = parseFloat(cleanedPrice) || 0;
    const inventory = ticketFormData.trackInventory ? (parseInt(ticketFormData.inventory) || 0) : undefined;

    const ticketPayload: TicketPayload = {
      title: ticketFormData.title.trim(),
      type: ticketFormData.type.trim() || 'normal',
      description: ticketFormData.description.trim(),
      price,
      currency: ticketFormData.currency,
      trackInventory: ticketFormData.trackInventory,
      inventory,
    };

    if (editingTicketIndex !== null) {
      const newTickets = [...tickets];
      newTickets[editingTicketIndex] = ticketPayload;
      setTickets(newTickets);
    } else {
      setTickets([...tickets, ticketPayload]);
    }

    setShowTicketModal(false);
    setEditingTicketIndex(null);
  };

  const handleCoverUpload = async (file: File) => {
    try {
      const coverFileObj = await convertFileToBase64(file);
      setCoverFile(coverFileObj);
      const preview = URL.createObjectURL(file);
      setCoverImage(preview);
    } catch (error) {
      toastError(getTranslation('organizerPanel.imageUploadError') || 'Resim yüklenemedi');
    }
  };

  const validateForm = (): { isValid: boolean; errors: Record<string, string> } => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = getTranslation('organizerPanel.titleRequired') || 'Etkinlik başlığı gereklidir';
    }
    if (!hasHtmlContent(description)) {
      newErrors.description = getTranslation('organizerPanel.descriptionRequired') || 'Etkinlik açıklaması gereklidir';
    }
    if (!coverFile) {
      newErrors.cover = getTranslation('organizerPanel.coverRequired') || 'Kapak görseli gereklidir';
    }
    if (!eventStartDate || !eventStartTime) {
      newErrors.eventStartDateTime = getTranslation('organizerPanel.startDateTimeRequired') || 'Başlangıç tarihi ve saati gereklidir';
    }
    if (!eventEndDate || !eventEndTime) {
      newErrors.eventEndDateTime = getTranslation('organizerPanel.endDateTimeRequired') || 'Bitiş tarihi ve saati gereklidir';
    }
    if (!isOnline && (!locationAddress || !locationLatitude || !locationLongitude)) {
      newErrors.location = getTranslation('organizerPanel.locationRequired') || 'Konum bilgileri gereklidir';
    }
    if (!isOnline && locationLatitude === 0 && locationLongitude === 0) {
      newErrors.location = getTranslation('organizerPanel.locationRequired') || 'Konum bilgileri gereklidir';
    }
    if (isOnline && !website.trim()) {
      newErrors.website = getTranslation('organizerPanel.websiteRequired') || 'Online etkinlikler için website gereklidir';
    }
    if (!ticketSaleStartDateTime || !ticketSaleTime) {
      newErrors.ticketSaleDateTime = getTranslation('organizerPanel.ticketSaleDateTimeRequired') || 'Bilet satış tarihi ve saati gereklidir';
    }
    if (participationType === 'ticket' && tickets.length === 0) {
      newErrors.tickets = getTranslation('organizerPanel.atLeastOneTicketRequired') || 'At least one ticket is required.';
    }

    // Validate date logic
    if (eventStartDate && eventStartTime && eventEndDate && eventEndTime) {
      const startDateTime = combineDateTime(eventStartDate, eventStartTime);
      const endDateTime = combineDateTime(eventEndDate, eventEndTime);
      const startTimestamp = Math.floor(new Date(startDateTime.replace(' ', 'T')).getTime() / 1000);
      const endTimestamp = Math.floor(new Date(endDateTime.replace(' ', 'T')).getTime() / 1000);
      
      if (endTimestamp <= startTimestamp) {
        newErrors.eventEndDateTime = getTranslation('organizerPanel.endDateAfterStart') || 'Bitiş tarihi başlangıç tarihinden sonra olmalıdır';
      }
    }

    setErrors(newErrors);
    return { isValid: Object.keys(newErrors).length === 0, errors: newErrors };
  };

  // Focus first error field
  const focusFirstError = (errorMap?: Record<string, string>) => {
    const errorKeys = Object.keys(errorMap || errors);
    if (errorKeys.length === 0) return;

    // Map error keys to field IDs/refs
    const fieldMap: Record<string, string> = {
      title: 'event-title-input',
      description: 'event-description-input',
      cover: 'event-cover-image',
      eventStartDateTime: 'event-start-date',
      eventEndDateTime: 'event-end-date',
      location: 'event-location-address',
      website: 'event-website-input',
      ticketSaleDateTime: 'event-ticket-sale-date',
      tickets: 'event-tickets-section',
    };

    const firstErrorKey = errorKeys[0];
    const fieldId = fieldMap[firstErrorKey];
    
    if (fieldId) {
      // Try to find the element by ID first
      let element = document.getElementById(fieldId);
      
      // If not found, try to find by data-field attribute
      if (!element) {
        element = document.querySelector(`[data-field="${firstErrorKey}"]`) as HTMLElement;
      }
      
      // For DatePicker and other components, try to find the input inside
      if (!element) {
        const container = document.getElementById(fieldId);
        if (container) {
          const input = container.querySelector('input') as HTMLElement;
          if (input) element = input;
        }
      }
      
      if (element) {
        setTimeout(() => {
          element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Try to focus the element, or find a focusable child
          if (element && typeof element.focus === 'function') {
            element.focus();
          } else if (element) {
            const focusable = element.querySelector('input, textarea, button') as HTMLElement;
            if (focusable && typeof focusable.focus === 'function') {
              focusable.focus();
            }
          }
        }, 100);
      }
    }
  };

  const handleSubmit = async () => {
    if (!token || !channel?.channelID) return;

    const validation = validateForm();
    if (!validation.isValid) {
      // Show toast message
      toastError(getTranslation('organizerPanel.pleaseFillAllNecessaryFields') || 'Lütfen tüm gerekli alanları doldurun');
      // Focus first error field
      setTimeout(() => {
        focusFirstError(validation.errors);
      }, 100);
      return;
    }

    setIsSubmitting(true);
    try {
      const eventStartDateTime = combineDateTime(eventStartDate, eventStartTime);
      const eventEndDateTime = combineDateTime(eventEndDate, eventEndTime);
      const ticketSaleStart = ticketSaleStartDateTime && ticketSaleTime
        ? combineDateTime(ticketSaleStartDateTime, ticketSaleTime)
        : new Date().toISOString().slice(0, 16).replace('T', ' ');

      const finalTickets = participationType === 'rsvp'
        ? [createLcvJoinTicket()]
        : tickets.filter((ticket) => !isLcvJoinTicketType(ticket.type));

      const socialMediaLinks = {
        website: "string", // mock value
        instagram: "string", // mock value
        x: "string", // mock value
      };

      // Calculate isFree based on tickets excluding sponsor and auto-generated LCV join ticket.
      // 1 = free, 2 = paid, 3 = mixed
      const ticketsForPricing = finalTickets.filter((ticket) => {
        const ticketType = (ticket.type || '').trim().toLowerCase();
        return ticketType !== 'sponsor' && !isLcvJoinTicketType(ticketType);
      });
      let calculatedIsFree = 1; // Default to free
      if (ticketsForPricing.length > 0) {
        const hasFreeTickets = ticketsForPricing.some(t => Number(t.price) === 0);
        const hasPaidTickets = ticketsForPricing.some(t => Number(t.price) > 0);

        if (hasFreeTickets && hasPaidTickets) {
          calculatedIsFree = 3; // Mixed
        } else if (hasPaidTickets) {
          calculatedIsFree = 2; // Paid
        } else {
          calculatedIsFree = 1; // Free
        }
      }

      const response = await Services.SocialHiveCreateEvent(
        token,
        calculatedIsFree,
        channel.channelID,
        title.trim(),
        description.trim(),
        coverFile,
        eventStartDateTime,
        eventEndDateTime,
        isOnline ? "1" : "0",
        isOnline ? website.trim() : "",
        !isOnline ? locationAddress.trim() : "",
        !isOnline ? String(locationLatitude) : "",
        !isOnline ? String(locationLongitude) : "",
        "string", // organizedBy - mock value
        !isOnline ? locationAddressDefinition.trim() : "",
        "1", // hasTickets - always "1" since tickets are required
        finalTickets,
        ticketSaleStart,
        "string", // contactEmail - mock value
        "string", // ticketSaleNotificationEmail - mock value
        '',
        "string", // contactPhone - mock value
        socialMediaLinks,
        true, // usersCanSendMessage - always true
        hasCertificate,
        isPublic,
        {}, // Empty settings
        !isOnline ? city.trim() : undefined // city - only for in-person events
      );

      if (response.data.status && response.data.result) {
        const createdEvent = response.data.result;
        toastSuccess(getTranslation('organizerPanel.eventCreated') || 'Etkinlik başarıyla oluşturuldu');
        handleCancelCreate();
        
        // Navigate to the created event's edit page
        // Use a small delay to ensure the event is fully created on the backend
        setTimeout(() => {
          const eventTitle = createdEvent.eventTitle || createdEvent.title;
          const channelSlug = channelTitle ? createSlug(channelTitle) : '';
          if (eventTitle && channelSlug && createdEvent.eventID) {
            navigate(generateEventAdminUrl(eventTitle, createdEvent.eventID, channelSlug), { replace: true });
          }
        }, 500);
      } else {
        throw new Error(response.data.message || 'Etkinlik oluşturulamadı');
      }
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || 
        error?.message || 
        getTranslation('organizerPanel.eventCreateError') || 'Etkinlik oluşturulamadı';
      toastError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEventClick = (event: EventListItem) => {
    const eventTitle = event.eventTitle || event.title;
    const channelSlug = channelTitle ? createSlug(channelTitle) : '';
    if (eventTitle && channelSlug) {
      navigate(generateEventAdminUrl(eventTitle, event.eventID, channelSlug));
    }
  };

  return (
    <div className={styles.organizerEventsPage}>
      <div className={styles.header}>
        {!isCreateFormOpen && (
        <Tabs variant="default">
          <Tab
            variant="default"
            isActive={activeTab === 'current'}
            onClick={() => setActiveTab('current')}
          >
            {getTranslation('organizerPanel.currentEvents') || 'Güncel Etkinlikler'}
          </Tab>
          <Tab
            variant="default"
            isActive={activeTab === 'past'}
            onClick={() => setActiveTab('past')}
          >
            {getTranslation('organizerPanel.pastEvents') || 'Geçmiş Etkinlikler'}
          </Tab>
        </Tabs>
        )}
        {isCreateFormOpen && (
          <div className={styles.headerButtons}>
            <Button
              variant="secondary"
              size="medium"
              onClick={handleCancelCreate}
              disabled={isSubmitting}
            >
              {getTranslation('common.cancel') || 'İptal'}
            </Button>
            <Button
              variant="primary"
              size="medium"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className={styles.createButton}
            >
              {isSubmitting
                ? (getTranslation('common.creating') || 'Oluşturuluyor...')
                : (getTranslation('organizerPanel.createEvent') || 'Etkinlik Oluştur')
              }
            </Button>
          </div>
        )}
        {!isCreateFormOpen && (
        <Button
          variant="primary"
          size="medium"
          onClick={handleCreateEvent}
          className={styles.createButton}
        >
          {getTranslation('organizerPanel.createEvent') || 'Etkinlik oluştur'}
        </Button>
        )}
      </div>

      {isCreateFormOpen && (
        <div className={styles.createFormContainer}>
          <div className={styles.createForm}>
            <h2 className={styles.formTitle}>
              {getTranslation('organizerPanel.createNewEvent') || 'Yeni Etkinlik Oluştur'}
            </h2>

            <ImageUploadModal
              isOpen={showCoverModal}
              onClose={() => setShowCoverModal(false)}
              onUpload={handleCoverUpload}
              title={getTranslation('organizerPanel.uploadCoverImage') || 'Kapak Görseli Yükle'}
              aspectRatio="16:9"
              recommendedSize="1920x1080"
              getTranslation={getTranslation}
            />

            {/* Ticket Modal */}
            <Modal
              isOpen={showTicketModal}
              onClose={() => {
                setShowTicketModal(false);
                setEditingTicketIndex(null);
              }}
              title={editingTicketIndex !== null 
                ? (getTranslation('organizerPanel.editTicket') || 'Bilet Düzenle')
                : (getTranslation('organizerPanel.addTicket') || 'Bilet Ekle')
              }
              variant="default"
            >
              <div className={formStyles.formFields}>
                <FormField
                  label={getTranslation('organizerPanel.ticketTitle') || 'Bilet Başlığı'}
                  required
                >
                  <FormInput
                    type="text"
                    value={ticketFormData.title}
                    onChange={(e) => setTicketFormData({ ...ticketFormData, title: e.target.value })}
                    placeholder={getTranslation('organizerPanel.ticketTitlePlaceholder') || 'Bilet başlığı'}
                  />
                </FormField>

                <FormField
                  label={getTranslation('organizerPanel.ticketType') || 'Bilet Tipi'}
                  required
                >
                  <FormInput
                    type="text"
                    value={ticketFormData.type}
                    onChange={(e) => {
                      const value = e.target.value;
                      const normalizedValue = value.trim().toLowerCase();
                      if (normalizedValue === 'sponsor') {
                        toastError(getTranslation('organizerPanel.ticketTypeSponsorNotAllowed') || 'Bilet tipi "sponsor" olamaz');
                        return;
                      }
                      if (isLcvJoinTicketType(value)) {
                        toastError(
                          getTranslation('organizerPanel.ticketTypeReservedName') ||
                          '"togather-join-ticket" rezerv bir bilet tipi adıdır.'
                        );
                        return;
                      }
                      setTicketFormData({ ...ticketFormData, type: value });
                    }}
                    placeholder={getTranslation('organizerPanel.ticketTypePlaceholder') || 'Bilet tipi (örn: Normal, VIP, Early Bird)'}
                  />
                </FormField>

                <FormField
                  label={getTranslation('organizerPanel.ticketDescription') || 'Açıklama'}
                  required
                >
                  <FormTextarea
                    value={ticketFormData.description}
                    onChange={(e) => setTicketFormData({ ...ticketFormData, description: e.target.value })}
                    rows={4}
                    placeholder={getTranslation('organizerPanel.ticketDescriptionPlaceholder') || 'Bilet açıklaması'}
                  />
                </FormField>

                <div className={formStyles.formRow}>
                  <FormField
                    label={getTranslation('organizerPanel.price') || 'Fiyat'}
                    required
                  >
                    <FormInput
                      type="number"
                      step="0.01"
                      min="0"
                      value={ticketFormData.price}
                      onChange={(e) => {
                        const value = e.target.value.replace(/[^\d.]/g, '');
                        setTicketFormData({ ...ticketFormData, price: value });
                      }}
                      placeholder="0"
                    />
                  </FormField>
                  <FormField
                    label={getTranslation('organizerPanel.currency') || 'Para Birimi'}
                  >
                    <FormInput
                      type="text"
                      value={ticketFormData.currency}
                      disabled
                      placeholder="TRY"
                    />
                  </FormField>
                </div>

                <FormField label={getTranslation('organizerPanel.trackInventory') || 'Envanter Takibi'}>
                  <label className={formStyles.toggleLabel}>
                    <input
                      type="checkbox"
                      className={formStyles.toggle}
                      checked={ticketFormData.trackInventory}
                      onChange={(e) => setTicketFormData({ 
                        ...ticketFormData, 
                        trackInventory: e.target.checked,
                        inventory: e.target.checked ? ticketFormData.inventory : '0'
                      })}
                    />
                    <span>{getTranslation('organizerPanel.trackInventory') || 'Envanter Takibi'}</span>
                  </label>
                </FormField>

                {ticketFormData.trackInventory && (
                  <FormField
                    label={getTranslation('organizerPanel.inventory') || 'Envanter'}
                    required
                  >
                    <FormInput
                      type="number"
                      min="0"
                      step="1"
                      value={ticketFormData.inventory}
                      onChange={(e) => setTicketFormData({ ...ticketFormData, inventory: e.target.value.replace(/\D/g, '') })}
                      placeholder="0"
                    />
                  </FormField>
                )}

                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                  <Button
                    variant="secondary"
                    size="medium"
                    onClick={() => {
                      setShowTicketModal(false);
                      setEditingTicketIndex(null);
                    }}
                  >
                    {getTranslation('common.cancel') || 'İptal'}
                  </Button>
                  <Button
                    variant="primary"
                    size="medium"
                    onClick={handleSaveTicket}
                  >
                    {getTranslation('common.save') || 'Kaydet'}
                  </Button>
                </div>
              </div>
            </Modal>

            <div className={formStyles.formFields}>
              {/* Cover Image */}
              <FormField
                label={getTranslation('organizerPanel.coverImage') || 'Kapak Görseli'}
                required
                error={errors.cover}
              >
                <div
                  id="event-cover-image"
                  className={styles.coverPlaceholder}
                  onClick={() => setShowCoverModal(true)}
                  data-field="cover"
                >
                  {coverImage ? (
                    <img src={coverImage} alt="Cover" className={styles.coverImage} />
                  ) : (
                    <div className={styles.uploadIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </FormField>

              {/* Title */}
              <FormField
                label={getTranslation('organizerPanel.eventTitle') || 'Etkinlik Başlığı'}
                required
                error={errors.title}
              >
                <FormInput
                  id="event-title-input"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder={getTranslation('organizerPanel.eventTitlePlaceholder') || 'Etkinlik başlığını girin'}
                  error={!!errors.title}
                  data-field="title"
                />
              </FormField>

              {/* Description */}
              <FormField
                label={getTranslation('organizerPanel.eventDescription') || 'Etkinlik Açıklaması'}
                required
                error={errors.description}
              >
                <RichTextEditor
                  id="event-description-input"
                  value={description}
                  onChange={(html) => setDescription(html)}
                  placeholder={getTranslation('organizerPanel.eventDescriptionPlaceholder') || 'Etkinlik açıklamasını girin'}
                  error={!!errors.description}
                  data-field="description"
                />
              </FormField>

              {/* Event Type Toggle */}
              <FormField label={getTranslation('organizerPanel.eventType') || 'Etkinlik Tipi'}>
                <label className={formStyles.toggleLabel}>
                  <input
                    type="checkbox"
                    className={formStyles.toggle}
                    checked={isOnline}
                    onChange={(e) => setIsOnline(e.target.checked)}
                  />
                  <span>{getTranslation('organizerPanel.onlineEvent') || 'Online Etkinlik'}</span>
                </label>
              </FormField>

              {/* Date and Time Fields */}
              <div className={formStyles.formRow}>
                <FormField
                  label={getTranslation('organizerPanel.startDate') || 'Başlangıç Tarihi'}
                  required
                  error={errors.eventStartDateTime}
                >
                  <DatePicker
                    id="event-start-date"
                    value={eventStartDate}
                    onChange={(value) => {
                      setEventStartDate(value);
                      // Set end date to same day if not already set or if it's before the new start date
                      if (value && (!eventEndDate || eventEndDate < value)) {
                        setEventEndDate(value);
                      }
                    }}
                    error={!!errors.eventStartDateTime}
                    locale={currentLanguage}
                    data-field="eventStartDateTime"
                  />
                </FormField>
                <FormField
                  label={getTranslation('organizerPanel.startTime') || 'Başlangıç Saati'}
                  required
                >
                  <TimePicker
                    value={eventStartTime}
                    onChange={(value) => {
                      setEventStartTime(value);
                      // Set end time to 3 hours later if not already set
                      if (value && !eventEndTime && eventStartDate) {
                        const result = addHoursToTimeWithDayCheck(value, 3);
                        setEventEndTime(result.time);
                        // If adding 3 hours crosses midnight, increment end date
                        if (result.crossedMidnight && eventStartDate) {
                          const startDate = new Date(eventStartDate);
                          startDate.setDate(startDate.getDate() + 1);
                          const year = startDate.getFullYear();
                          const month = String(startDate.getMonth() + 1).padStart(2, '0');
                          const day = String(startDate.getDate()).padStart(2, '0');
                          setEventEndDate(`${year}-${month}-${day}`);
                        }
                      }
                    }}
                    error={!!errors.eventStartDateTime}
                    locale={currentLanguage}
                  />
                </FormField>
              </div>

              <div className={formStyles.formRow}>
                <FormField
                  label={getTranslation('organizerPanel.endDate') || 'Bitiş Tarihi'}
                  required
                  error={errors.eventEndDateTime}
                >
                  <DatePicker
                    id="event-end-date"
                    value={eventEndDate}
                    onChange={(value) => setEventEndDate(value)}
                    error={!!errors.eventEndDateTime}
                    minDate={eventStartDate || undefined}
                    locale={currentLanguage}
                    data-field="eventEndDateTime"
                  />
                </FormField>
                <FormField
                  label={getTranslation('organizerPanel.endTime') || 'Bitiş Saati'}
                  required
                >
                  <TimePicker
                    value={eventEndTime}
                    onChange={(value) => setEventEndTime(value)}
                    error={!!errors.eventEndDateTime}
                    locale={currentLanguage}
                  />
                </FormField>
              </div>

              {/* Location or Website */}
              {isOnline ? (
                <FormField
                  label={getTranslation('organizerPanel.eventLink') || 'Etkinlik Linki'}
                  required
                  error={errors.website}
                >
                  <FormInput
                    id="event-website-input"
                    type="url"
                    value={website}
                    onChange={(e) => setWebsite(e.target.value)}
                    placeholder={getTranslation('organizerPanel.eventLinkPlaceholder') || 'https://...'}
                    error={!!errors.website}
                    data-field="website"
                  />
                </FormField>
              ) : (
                <>
                  <FormField
                    label={getTranslation('organizerPanel.locationAddress') || 'Konum Adresi'}
                    required
                    error={errors.location}
                  >
                    <GooglePlacesAutocomplete
                      id="event-location-address"
                      value={locationAddress}
                      onChange={(shortAddress, fullAddress, lat, lng, extractedCity) => {
                        setLocationAddress(shortAddress);
                        if (lat !== 0 && lng !== 0) {
                          setLocationAddressDefinition(fullAddress);
                          setLocationLatitude(lat);
                          setLocationLongitude(lng);
                          if (extractedCity) {
                            setCity(extractedCity);
                          }
                        }
                      }}
                      placeholder={getTranslation('organizerPanel.locationAddressPlaceholder') || 'Adres arayın veya haritadan seçin...'}
                      data-field="location"
                    />
                  </FormField>

                  <FormField
                    label={getTranslation('organizerPanel.fullAddress') || 'Tam Adres'}
                  >
                    <FormTextarea
                      value={locationAddressDefinition}
                      onChange={(e) => setLocationAddressDefinition(e.target.value)}
                      rows={3}
                      placeholder={getTranslation('organizerPanel.fullAddressPlaceholder') || 'Tam adres bilgisi otomatik olarak doldurulacaktır'}
                    />
                  </FormField>

                  <FormField
                    label={getTranslation('organizerPanel.city') || 'Şehir'}
                  >
                    <FormInput
                      type="text"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      placeholder={getTranslation('organizerPanel.cityPlaceholder') || 'Şehir adı girin'}
                    />
                  </FormField>

                  <div className={formStyles.formRow}>
                    <FormField
                      label={getTranslation('organizerPanel.latitude') || 'Enlem'}
                      required
                    >
                      <FormInput
                        type="number"
                        step="any"
                        value={locationLatitude && !isNaN(locationLatitude) ? locationLatitude : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocationLatitude(value === '' ? 0 : Number(value));
                        }}
                        placeholder={getTranslation('organizerPanel.latitudePlaceholder') || '41.0082'}
                      />
                    </FormField>
                    <FormField
                      label={getTranslation('organizerPanel.longitude') || 'Boylam'}
                      required
                    >
                      <FormInput
                        type="number"
                        step="any"
                        value={locationLongitude && !isNaN(locationLongitude) ? locationLongitude : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setLocationLongitude(value === '' ? 0 : Number(value));
                        }}
                        placeholder={getTranslation('organizerPanel.longitudePlaceholder') || '28.9784'}
                      />
                    </FormField>
                  </div>

                  {!isOnline && locationLatitude !== 0 && locationLongitude !== 0 && (
                    <div className={styles.mapContainer}>
                      <GoogleMap
                        latitude={locationLatitude}
                        longitude={locationLongitude}
                        address={locationAddress}
                        interactive={true}
                        onLocationSelect={(lat, lng) => {
                          setLocationLatitude(lat);
                          setLocationLongitude(lng);
                          // Reverse geocode to get address
                          if (window.google && window.google.maps) {
                            const geocoder = new window.google.maps.Geocoder();
                            geocoder.geocode({ location: { lat, lng } }, (results: any, status: any) => {
                              if (status === 'OK' && results && results[0]) {
                                if (!locationAddress.trim()) {
                                  setLocationAddress(results[0].name || results[0].formatted_address);
                                }
                                setLocationAddressDefinition(results[0].formatted_address);
                                // Extract city from address_components
                                const addressComponents = results[0].address_components;
                                if (addressComponents) {
                                  let locality, adminLevel2, adminLevel1;
                                  for (const component of addressComponents) {
                                    const types = component.types || [];
                                    if (types.includes('locality')) locality = component.long_name;
                                    if (types.includes('administrative_area_level_2')) adminLevel2 = component.long_name;
                                    if (types.includes('administrative_area_level_1')) adminLevel1 = component.long_name;
                                  }
                                  // For Turkish addresses, combine district and province
                                  if (adminLevel2 && adminLevel1 && !locality) {
                                    setCity(`${adminLevel2} / ${adminLevel1}`);
                                  } else if (locality) {
                                    setCity(adminLevel1 && adminLevel1 !== locality ? `${locality}, ${adminLevel1}` : locality);
                                  } else if (adminLevel2 || adminLevel1) {
                                    setCity(adminLevel2 || adminLevel1);
                                  }
                                }
                              }
                            });
                          }
                        }}
                      />
                    </div>
                  )}
                </>
              )}


              {/* Ticket Sale Date/Time */}
              <div className={formStyles.formRow}>
                <FormField
                  label={getTranslation('organizerPanel.ticketSaleStartDate') || 'Bilet Satış Başlangıç Tarihi'}
                  required
                  error={errors.ticketSaleDateTime}
                >
                  <DatePicker
                    id="event-ticket-sale-date"
                    value={ticketSaleStartDateTime}
                    onChange={(value) => setTicketSaleStartDateTime(value)}
                    error={!!errors.ticketSaleDateTime}
                    locale={currentLanguage}
                    data-field="ticketSaleDateTime"
                  />
                </FormField>
                <FormField
                  label={getTranslation('organizerPanel.ticketSaleStartTime') || 'Bilet Satış Başlangıç Saati'}
                  required
                >
                  <TimePicker
                    value={ticketSaleTime}
                    onChange={(value) => setTicketSaleTime(value)}
                    error={!!errors.ticketSaleDateTime}
                    locale={currentLanguage}
                  />
                </FormField>
              </div>

              <FormField label={getTranslation('organizerPanel.participationType') || 'Katılım Tipi'}>
                <div className={formStyles.radioGroup}>
                  <label className={formStyles.radioLabel}>
                    <input
                      type="radio"
                      name="participationType"
                      className={formStyles.radio}
                      checked={participationType === 'ticket'}
                      onChange={() => setParticipationType('ticket')}
                    />
                    <span>{getTranslation('organizerPanel.participationTypeTicket') || 'Bilet'}</span>
                  </label>
                  <label className={formStyles.radioLabel}>
                    <input
                      type="radio"
                      name="participationType"
                      className={formStyles.radio}
                      checked={participationType === 'rsvp'}
                      onChange={() => setParticipationType('rsvp')}
                    />
                    <span>{getTranslation('organizerPanel.participationTypeRsvp') || 'LCV'}</span>
                  </label>
                </div>
              </FormField>
              <p className={styles.participationHelp}>
                {participationType === 'rsvp'
                  ? (getTranslation('organizerPanel.participationTypeHelpRsvp') || 'LCV secildiginde etkinlik detayinda sadece Katil butonu gorunur ve ozel bilet eklenemez.')
                  : (getTranslation('organizerPanel.participationTypeHelpTicket') || 'Bilet secildiginde katilimcilar sadece bilet alarak etkinlige katilir.')}
              </p>

              <FormField label={getTranslation('organizerPanel.hasCertificateQuestion') || 'Bu etkinliğin sonunda katılımcılar sertifika alabilir mi?'}>
                <div className={formStyles.radioGroup}>
                  <label className={formStyles.radioLabel}>
                    <input
                      type="radio"
                      name="hasCertificate"
                      className={formStyles.radio}
                      checked={hasCertificate === true}
                      onChange={() => setHasCertificate(true)}
                    />
                    <span>{getTranslation('common.yes') || 'Evet'}</span>
                  </label>
                  <label className={formStyles.radioLabel}>
                    <input
                      type="radio"
                      name="hasCertificate"
                      className={formStyles.radio}
                      checked={hasCertificate === false}
                      onChange={() => setHasCertificate(false)}
                    />
                    <span>{getTranslation('common.no') || 'Hayır'}</span>
                  </label>
                </div>
              </FormField>

              {/* Tickets List */}
              <div
                id="event-tickets-section"
                className={`${styles.ticketsSection} ${participationType === 'rsvp' ? styles.ticketsSectionDisabled : ''}`}
                data-field="tickets"
              >
                <div className={styles.ticketsHeader}>
                  <h3 className={styles.ticketsTitle}>
                    {getTranslation('organizerPanel.tickets') || 'Biletler'} *
                  </h3>
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={handleAddTicket}
                    disabled={participationType === 'rsvp'}
                  >
                    {getTranslation('organizerPanel.addTicket') || 'Bilet Ekle'}
                  </Button>
                </div>

                {participationType === 'rsvp' ? (
                  <p className={styles.noTickets}>
                    {getTranslation('organizerPanel.rsvpDisablesTickets') || 'LCV seciliyken ozel bilet ekleyemezsiniz.'}
                  </p>
                ) : tickets.length === 0 ? (
                  <p className={errors.tickets ? styles.noTicketsError : styles.noTickets}>
                    {(errors.tickets && participationType === 'ticket')
                      ? errors.tickets
                      : (getTranslation('organizerPanel.noTickets') || 'Henüz bilet eklenmemiş')}
                  </p>
                ) : (
                  <div className={styles.ticketsList}>
                    {tickets.map((ticket, index) => (
                      <div key={index} className={styles.ticketItem}>
                        <div className={styles.ticketInfo}>
                          <h4 className={styles.ticketTitle}>{ticket.title}</h4>
                          <p className={styles.ticketMeta}>
                            {ticket.type} • {ticket.price} {ticket.currency}
                            {ticket.trackInventory && ` • ${ticket.inventory} ${getTranslation('organizerPanel.inventory') || 'Envanter'}`}
                          </p>
                        </div>
                        <div className={styles.ticketActions}>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => handleEditTicket(index)}
                          >
                            {getTranslation('common.edit') || 'Düzenle'}
                          </Button>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => handleDeleteTicket(index)}
                          >
                            {getTranslation('common.delete') || 'Sil'}
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Event Visibility Dropdown */}
              <FormField label={getTranslation('organizerPanel.eventVisibility') || 'Etkinlik Görünürlüğü'}>
                <select
                  value={isPublic}
                  onChange={(e) => setIsPublic(e.target.value as "0" | "1" | "3")}
                  className={formStyles.select}
                >
                  <option value="1">{getTranslation('organizerPanel.eventVisibilityPublic') || 'Public - Anyone can discover this event'}</option>
                  <option value="0">{getTranslation('organizerPanel.eventVisibilityPrivate') || 'Private - Only community members can see'}</option>
                  <option value="3">{getTranslation('organizerPanel.eventVisibilityUnlisted') || 'Unlisted - Only people with the link can access'}</option>
                </select>
              </FormField>
            </div>
          </div>
        </div>
      )}

      {!isCreateFormOpen && loading && (
        <div className={styles.eventsList}>
          <Table>
            <TableHeader
              columns={[
                getTranslation('organizerPanel.eventName') || 'Etkinlik Adı',
                getTranslation('organizerPanel.dateRange') || 'Tarih Aralığı',
                getTranslation('organizerPanel.participants') || 'Katılımcılar'
              ]}
              columnWidths={['2fr', '1fr', '0.5fr']}
              columnAlignments={['left', 'left', 'right']}
            />
            <TableBody>
              {Array.from({ length: 5 }).map((_, index) => (
                <TableRow key={`skeleton-${index}`} columnWidths={['2fr', '1fr', '0.5fr']}>
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <LoadingSkeleton height={60} width={107} variant="rectangular" />
                      <div style={{ flex: 1 }}>
                        <div style={{ marginBottom: '8px' }}>
                          <LoadingSkeleton height={20} width="70%" />
                        </div>
                        <LoadingSkeleton height={16} width="50%" />
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <LoadingSkeleton height={16} width="80%" />
                    <div style={{ marginTop: '4px' }}>
                      <LoadingSkeleton height={14} width="60%" />
                    </div>
                  </TableCell>
                  <TableCell>
                    <LoadingSkeleton height={16} width="40%" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
      {!isCreateFormOpen && !loading && (
        <div key={activeTab} className={`${styles.eventsList} ${transitionStyles.fadeIn}`}>
          <Table>
            <TableHeader
              columns={[
                getTranslation('organizerPanel.eventName') || 'Etkinlik Adı',
                getTranslation('organizerPanel.dateRange') || 'Tarih Aralığı',
                getTranslation('organizerPanel.participants') || 'Katılımcılar'
              ]}
              columnWidths={['2fr', '1fr', '0.5fr']}
              columnAlignments={['left', 'left', 'right']}
            />
            <TableBody>
              {displayedEvents.length === 0 ? (
                <EmptyTableRow
                  message={
                    activeTab === 'current'
                      ? getTranslation('organizerPanel.noCurrentEvents') || 'Güncel etkinlik bulunamadı'
                      : getTranslation('organizerPanel.noPastEvents') || 'Geçmiş etkinlik bulunamadı'
                  }
                />
              ) : (
                displayedEvents.map((event) => {
                  const eventTitle = event.eventTitle || event.title;
                  const eventCover = event.cover || event.image || event.bannerImage;
                  const isOnline = event.isOnline === "1";
                  const visibilityLabel = event.isPublic === "3"
                    ? (getTranslation('organizerPanel.unlisted') || 'Unlisted')
                    : event.isPublic === "1"
                      ? (getTranslation('organizerPanel.public') || 'Herkese Açık')
                      : (getTranslation('organizerPanel.private') || 'Özel');
                  const locationAddress = event.locationAddress || '';
                  const startDate = event.eventStartDateTime ? new Date(event.eventStartDateTime * 1000) : null;
                  const endDate = event.eventEndDateTime ? new Date(event.eventEndDateTime * 1000) : null;
                  const participantCount = event.soldTicketCount ?? 0;

                  return (
                    <TableRow
                      key={event.eventID}
                      columnWidths={['2fr', '1fr', '0.5fr']}
                      onClick={() => handleEventClick(event)}
                    >
                      <TableCell>
                        <div className={styles.eventInfo}>
                          {eventCover && (
                            <img
                              src={eventCover}
                              alt={eventTitle}
                              className={styles.eventImage}
                              onError={(e) => {
                                (e.target as HTMLImageElement).style.display = 'none';
                              }}
                            />
                          )}
                          <div className={styles.eventDetails}>
                            <h3 className={styles.eventTitle}>{eventTitle}</h3>
                            <p className={styles.eventMeta}>
                              {isOnline
                                ? getTranslation('events.online') || 'Online'
                                : locationAddress || getTranslation('events.location') || 'Yerinde'}
                              {', '}
                              {visibilityLabel}
                            </p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {startDate && endDate && (
                          <div className={styles.dateRange}>
                            {(() => {
                              const isSameDay = startDate.toDateString() === endDate.toDateString();

                              if (isSameDay) {
                                return (
                                  <>
                                    <div className={styles.dateText}>
                                      {formatDateShort(startDate, currentLanguage)}
                                    </div>
                                    <div className={styles.timeText}>
                                      {formatTime(startDate)}-{formatTime(endDate)}
                                    </div>
                                  </>
                                );
                              } else {
                                return (
                                  <>
                                    <div className={styles.dateText}>
                                      {formatDateShort(startDate, currentLanguage)} - {formatDateShort(endDate, currentLanguage)}
                                    </div>
                                    <div className={styles.timeText}>
                                      {formatTime(startDate)}-{formatTime(endDate)}
                                    </div>
                                  </>
                                );
                              }
                            })()}
                          </div>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <div className={styles.participantBadge}>
                          {/* @ts-ignore */}
                          <HiUsers size={16} />
                          <span>{participantCount}</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default OrganizerEventsPage;
